// Hændelseskortet: naboprotest, spidsbølge, hedebølge og datacenter-kø. 2-3 svar og en nedtælling.
// Svarer ingen, vælger kortet selv sit standardsvar, når tiden er gået (sim/threats.ts).
// Kortet glider ind i bunden (et fast ark), og det svar, træneren foreslår, lyser op med "Prøv den".

import type { Game } from '../game';
import type { CardOption, State, Threat } from '../sim';
import { TICK_HZ } from '../sim';
import { check } from '../sim/actions';
import { heatAirPm, nodeById } from '../sim/grid';
import type { Rect } from '../render/camera';
import { CARD_COST, cardOpen } from '../sim/threats';
import { h, kbd, setAttr, setText, toggle } from './dom';
import { CARD_PARTNER, CARD_PAUSED, CARD_PAUSED_PARTNER, CARD_PEEK, THREAT_NAMES } from './text';

export interface CardHandlers {
  answer(threat: string, opt: CardOption): void;
  /** Kortet venter som fane bag en menu: et tryk lukker menuen og henter kortet frem. */
  front(): void;
}

/** Svarets navn og hvad det gør, i et par ord. */
export const CARD_OPTION: Record<CardOption, { label: string; what: string }> = {
  cable: { label: 'Kabelstykke', what: 'Naboerne er glade' },
  meeting: { label: 'Borgermøde', what: 'Ledningen kører halvt i 20 s' },
  build: { label: 'Byg alligevel', what: 'Byen bliver sur' },
  smart: { label: 'Smart opladning', what: 'Halv bølge, dobbelt så længe' },
  // IGN-løkke runde 3: "Bærer nettet bølgen: +3 kr" var kryptisk. Nu står betingelsen som en sætning.
  ride: { label: 'Hold den ud', what: 'Klarer byerne den, får du 3 kr' },
  plan: { label: 'Planlæg køling', what: 'Byerne bruger ikke mere' },
  reserve: { label: 'Reservér plads', what: 'Fantomer afsløres, 30 s ro' },
  wait: { label: 'Vent og se', what: '10 s til at koble dem på' },
};

/** Kortets tekst: hvad der sker, kort og varmt. */
export function cardText(state: State, t: Threat): string {
  const name = (id: string | undefined): string => (id ? (nodeById(state, id)?.name ?? '') : '');
  const n = t.nodes?.length ?? 0;
  switch (t.kind) {
    case 'protest': {
      // Playtest runde 3: uklart, hvilken mast protesten handler om. Kortet nævner ledningen.
      const city = t.nodes?.[0];
      const l = t.targetLine ? state.lines.find((x) => x.id === t.targetLine) : undefined;
      if (!l) return `Naboerne ved ${name(city)} vil ikke have master.`;
      const other = l.a === city ? l.b : l.b === city ? l.a : undefined;
      return other
        ? `Naboerne ved ${name(city)} vil ikke have masterne mod ${name(other)}.`
        : `Naboerne ved ${name(city)} vil ikke have masterne på ${name(l.a)}–${name(l.b)}.`;
    }
    case 'spike':
      return n === 1 ? `Elbilerne i ${name(t.nodes?.[0])} lader på én gang: +${t.pct ?? 30}\u00a0%.` : `Elbilerne lader på én gang i ${n} byer: +${t.pct ?? 30}\u00a0%.`;
    case 'heatwave':
      return `Luftledninger bærer kun ${heatAirPm(t.strength) / 10}\u00a0%, vinden løjer af, og byerne vil have køling.`;
    case 'dc_rush':
      return n === 1 ? 'Et datacenter står i kø. Måske er det et fantom.' : `${n} datacentre står i kø. Nogle er måske fantomer.`;
    default:
      return '';
  }
}

/** Genvejstaster til kortets svar, fra venstre mod højre. */
export const CARD_KEYS = ['A', 'S', 'D'];

interface OptBtn {
  el: HTMLButtonElement;
  label: HTMLElement;
  what: HTMLElement;
}

export class CardPanel {
  readonly el: HTMLElement;
  private title = h('span', { class: 'evcard-title' });
  private time = h('span', { class: 'evcard-time' });
  private text = h('p', { class: 'evcard-text' });
  private opts = h('div', { class: 'evcard-opts' });
  private foot = h('p', { class: 'evcard-foot' });
  private bar = h('span', { class: 'evcard-bar' });
  /** Fanen, kortet står som bag spillerens egen menu: aldrig usynligt (IGN-løkke 2, runde 3). */
  private peekBtn = h('button', { type: 'button', class: 'evcard-peek', hidden: true, onclick: () => this.handlers.front() }, CARD_PEEK);
  private btns: OptBtn[] = [];
  /** Står kortet som fane bag en menu (fokuslaget)? Sættes af main hver frame. */
  peek = false;
  /** Hvem svarer, når kortet er makkerens: "Volt" i solo, "Driftsvagten" i rummet. */
  partner = 'Driftsvagten';
  private shown = '';
  private want = '';
  /** Volts forslag pr. kort (M3): knappen får mærket "Volts bud". */
  private picks = new Map<string, CardOption>();
  /** Trænerens forslag: knappen lyser op (i stedet for en hånd, der dækkede den). */
  private tipOpt: CardOption | null = null;
  /** Fodnoten, når uret går: hvad der sker, hvis ingen svarer. */
  private footText = '';

  constructor(private handlers: CardHandlers) {
    this.el = h(
      'section',
      { class: 'evcard', hidden: true, role: 'group', 'aria-label': 'Hændelseskort' },
      h('div', { class: 'evcard-head' }, this.title, this.time),
      this.text,
      this.opts,
      this.foot,
      this.peekBtn,
      h('span', { class: 'evcard-track', 'aria-hidden': 'true' }, this.bar),
    );
  }

  /** Vis netop dette kort (tryk på figuren på kortet). */
  focus(id: string): void {
    this.want = id;
    this.el.classList.remove('flash');
    void this.el.offsetWidth;
    this.el.classList.add('flash');
    this.btns[0]?.el.focus({ preventScroll: true });
  }

  /** Volt foreslår et svar på kortet. Spilleren vælger stadig selv. */
  suggest(threat: string, opt: CardOption | null): void {
    if (opt) this.picks.set(threat, opt);
    else this.picks.delete(threat);
  }

  /** Træneren foreslår dette svar (første kort): knappen lyser op. Null slukker. */
  tip(opt: CardOption | null): void {
    this.tipOpt = opt;
  }

  /** Svar nummer i (0-baseret) fra tastaturet. Sand, hvis et kort var fremme. */
  answerIndex(i: number): boolean {
    if (this.el.hidden || !this.shown) return false;
    const b = this.btns[i]?.el;
    if (b && !b.disabled) b.click();
    return true;
  }

  /** Er et kort fremme lige nu (helt, ikke som fane)? */
  get isOpen(): boolean {
    return !this.el.hidden && this.shown !== '' && !this.peek;
  }

  /** Står kortet synligt som fane bag en menu? */
  get isPeeking(): boolean {
    return !this.el.hidden && this.shown !== '' && this.peek;
  }

  /** Venter et kort på svar, også mens en menu står foran det (fokuslaget)? */
  get pending(): boolean {
    return this.shown !== '';
  }

  /**
   * Kortet står altid samme sted: et ark i bunden (CSS). Layoutet giver kun, hvor højt det højst må
   * blive (under HUD'en), så knapperne aldrig skubbes ud af skærmen på en lav telefon på siden.
   */
  place(maxH: number): void {
    this.el.style.maxHeight = `${Math.max(140, Math.round(maxH))}px`;
  }

  /** Et udfald står fremme (messages.ts): kortet træder et øjeblik til side, men tæller videre. */
  hold(on: boolean): void {
    toggle(this.el, 'held', on);
  }

  /** Hvor kortet står på skærmen (px), uden ind-animationen. Null, når det er væk. */
  rect(): Rect | null {
    if (!this.isOpen) return null;
    const e = this.el;
    const w = e.offsetWidth;
    if (w <= 0) return null;
    return { x: e.offsetLeft, y: e.offsetTop, w, h: e.offsetHeight };
  }

  /** `defer`: midt i et træk venter et nyt kort, til fingeren slipper (playtest: kortet åd trækket). */
  update(game: Game, covered: boolean, defer = false): void {
    const s = game.state;
    const open = s.threats.filter(cardOpen);
    const t = open.find((x) => x.id === this.want) ?? open.slice().sort((a, b) => a.hitAt - b.hitAt)[0];
    const wait = defer && !!t && t.id !== this.shown;
    this.el.hidden = covered || !t || wait;
    if (wait) return;
    if (!t || !t.card) {
      this.shown = '';
      return;
    }
    if (t.id !== this.shown) this.build(t);
    toggle(this.el, 'peek', this.peek);
    this.peekBtn.hidden = !this.peek;
    // Står uret stille bag kortet, er der ingen nedtælling at vise: "N s" ved siden af "Tiden står
    // stille" lød som en modsigelse (IGN runde 3). Tiden og stregen kommer igen, når uret går.
    toggle(this.el, 'frozen', game.cardHold);
    const left = Math.max(0, t.hitAt - s.clockTick);
    setText(this.time, this.peek ? 'venter' : game.cardHold ? '' : `${Math.ceil(left / TICK_HZ)} s`);
    this.bar.style.width = `${Math.round((left / Math.max(1, t.hitAt - t.warnAt)) * 100)}%`;
    // Makkerspillets roller: kortet er Driftsvagtens. Planlæggeren ser det, men svarer ikke.
    const theirs = game.theirs('answer_card');
    // Solo står uret stille bag kortet: så er der intet standardsvar at frygte, kun et valg at træffe.
    // Svarer Volt, er valget hans, ikke spillerens (IGN runde 3: "til du har valgt" stod, mens Volt valgte).
    // Uret går (Volt i solo, makkeren i rummet): "Svarer du ikke" talte til den forkerte.
    const deflt = t.card ? CARD_OPTION[t.card.deflt].label.toLowerCase() : '';
    setText(this.foot, game.cardHold ? (theirs ? CARD_PAUSED_PARTNER(this.partner) : CARD_PAUSED) : theirs ? CARD_PARTNER(this.partner, deflt) : this.footText);
    setText(this.text, theirs ? `${cardText(s, t)} ${this.partner} svarer.` : cardText(s, t));
    for (let i = 0; i < this.btns.length; i++) {
      const b = this.btns[i] as OptBtn;
      const o = t.card.options[i] as CardOption;
      const ok = check(s, 'answer_card', t.id, o, game.player).ok;
      b.el.disabled = !ok || theirs;
      toggle(b.el, 'volt-pick', this.picks.get(t.id) === o);
      toggle(b.el, 'tip', this.tipOpt === o && ok && !theirs);
      const cost = CARD_COST[o];
      setAttr(b.el, 'aria-label', `${CARD_OPTION[o].label}, ${cost > 0 ? `${cost} kroner` : 'gratis'}. ${CARD_OPTION[o].what}.`);
    }
  }

  private build(t: Threat): void {
    if (!t.card) return;
    this.shown = t.id;
    setText(this.title, THREAT_NAMES[t.kind] ?? 'Hændelse');
    this.opts.replaceChildren();
    this.btns = t.card.options.map((o) => {
      const cost = CARD_COST[o];
      const label = h('span', { class: 'evcard-label' }, CARD_OPTION[o].label);
      const what = h('span', { class: 'evcard-what' }, `${cost > 0 ? `${cost} kr` : 'Gratis'} · ${CARD_OPTION[o].what}`);
      const el = h('button', { type: 'button', class: 'evcard-opt', 'data-opt': o, onclick: () => this.handlers.answer(t.id, o) }, label, what);
      const key = CARD_KEYS[this.opts.childElementCount];
      if (key) kbd(el, key, key.toLowerCase(), `${CARD_OPTION[o].label}: ${CARD_OPTION[o].what}`);
      this.opts.append(el);
      return { el, label, what };
    });
    this.footText = `Svarer du ikke: ${CARD_OPTION[t.card.deflt].label.toLowerCase()}.`;
    // Et nyt kort vendes ind, også når det afløser et andet.
    this.el.classList.remove('in', 'flash');
    void this.el.offsetWidth;
    this.el.classList.add('in');
  }
}
